import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { api, formatApiError, getUtm, trackEvent } from "../../lib/api";

export const CATEGORIES = [
  { value: "grow-my-brand", label: "Grow my brand" },
  { value: "digital-product", label: "Explore a digital product" },
  { value: "travel-experience", label: "Plan a travel experience" },
  { value: "partnership", label: "Affiliate / strategic partnership" },
  { value: "general", label: "Something else" },
  { value: "help-support", label: "Help & support" },
];

const BUSINESS_CATS = ["grow-my-brand", "partnership", "digital-product"];
const SUPPORT_CATS = ["general", "help-support"];

export const EnquiryForm = ({ defaultCategory = "", venture = "", title = "Send an enquiry", dark = true, sourcePage }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", category: defaultCategory, city: "", venture_interest: "", company: "",
    website: "", budget_range: "", message: "", privacy_consent: false, promo_consent: false,
    whatsapp_consent: false, company_url: "",
  });
  const [ventureOptions, setVentureOptions] = useState([]);
  const [state, setState] = useState({ status: "idle", error: "", id: "" });
  const startedRef = useRef(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!venture) {
      api.get("/content/ventures").then((r) => setVentureOptions(r.data.map((v) => ({ slug: v.slug, name: v.name })))).catch(() => {});
    }
  }, [venture]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("form_start", { category: form.category || defaultCategory || "unset" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setState({ status: "submitting", error: "", id: "" });
    try {
      const payload = {
        ...form,
        category: form.category || "general",
        message: form.message || `${form.category || "general"} enquiry from ${form.name}`,
        venture: venture || form.venture_interest || undefined,
        source_page: sourcePage || window.location.pathname,
        ...getUtm(),
      };
      const { data } = await api.post("/leads", payload);
      trackEvent("form_submit", { category: payload.category, venture: venture || "" });
      if (typeof window.fbq === "function") window.fbq("track", "Lead");
      if (typeof window.gtag === "function") window.gtag("event", "generate_lead", { event_category: "enquiry", event_label: payload.category });
      setState({ status: "success", error: "", id: data.id || "" });
    } catch (err) {
      setState({ status: "error", error: formatApiError(err), id: "" });
    } finally {
      submittingRef.current = false;
    }
  };

  const fieldCls = dark ? "input-ek" : "w-full rounded-xl border border-midnight/10 bg-white px-4 py-3 text-sm text-slatetext placeholder:text-slate-400 transition-[border-color] duration-300 focus:border-violet/60 focus:outline-none";
  const labelCls = dark ? "text-slate-400" : "text-slatetext/70";
  const isCafe = form.category === "cafe-updates";
  const showBusiness = BUSINESS_CATS.includes(form.category);
  const routedTo = SUPPORT_CATS.includes(form.category) ? "helpdesk@ekavrixtech.com" : "business@ekavrixtech.com";

  if (state.status === "success") {
    return (
      <div data-testid="enquiry-success" className={`rounded-2xl border p-8 text-center ${dark ? "border-teal/30 bg-teal/[0.06]" : "border-teal/40 bg-teal/[0.05]"}`}>
        <CheckCircle2 className="mx-auto text-teal" size={40} />
        <h3 className={`mt-4 font-display text-2xl font-semibold ${dark ? "text-mist" : "text-midnight"}`}>
          {isCafe ? "You are on the list" : "Enquiry received"}
        </h3>
        <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-slate-400" : "text-slatetext/70"}`}>
          {isCafe
            ? `Thank you, ${form.name.split(" ")[0]}. Your details are saved and you will be among the first to hear when cafe launch details are confirmed.`
            : `Thank you, ${form.name.split(" ")[0]}. Your enquiry has been saved and routed to our team. Expect a considered reply, usually within two working days.`}
        </p>
        {state.id && <p className={`mt-3 font-mono text-xs ${dark ? "text-slate-500" : "text-slatetext/50"}`}>Reference: {state.id.slice(0, 8).toUpperCase()}</p>}
      </div>
    );
  }

  return (
    <form data-testid="enquiry-form" onSubmit={submit} className="space-y-4" noValidate={false}>
      <h3 className={`font-display text-xl font-semibold ${dark ? "text-mist" : "text-midnight"}`}>{title}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="eq-name" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>Name *</label>
          <input id="eq-name" data-testid="enquiry-name" required minLength={2} value={form.name} onChange={set("name")} className={fieldCls} placeholder="Your name" />
        </div>
        <div>
          <label htmlFor="eq-email" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>Email *</label>
          <input id="eq-email" data-testid="enquiry-email" required type="email" value={form.email} onChange={set("email")} className={fieldCls} placeholder="you@company.com" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="eq-phone" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>
            Phone / WhatsApp {isCafe ? "" : "(optional)"}
          </label>
          <input id="eq-phone" data-testid="enquiry-phone" required={isCafe && form.whatsapp_consent} value={form.phone} onChange={set("phone")} className={fieldCls} placeholder="+91 ..." />
        </div>
        <div>
          <label htmlFor="eq-category" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>What is this about? *</label>
          <select id="eq-category" data-testid="enquiry-category" required value={form.category} onChange={set("category")} className={fieldCls}>
            <option value="" disabled>Select a category</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>
      {!venture && ventureOptions.length > 0 && (
        <div>
          <label htmlFor="eq-venture" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>Venture of interest (optional)</label>
          <select id="eq-venture" data-testid="enquiry-venture" value={form.venture_interest} onChange={set("venture_interest")} className={fieldCls}>
            <option value="">General enquiry</option>
            {ventureOptions.map((v) => <option key={v.slug} value={v.slug}>{v.name}</option>)}
          </select>
        </div>
      )}
      {isCafe && (
        <div>
          <label htmlFor="eq-city" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>City *</label>
          <input id="eq-city" data-testid="enquiry-city" required value={form.city} onChange={set("city")} className={fieldCls} placeholder="Your city" />
        </div>
      )}
      {showBusiness && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="eq-company" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>Company (optional)</label>
            <input id="eq-company" data-testid="enquiry-company" value={form.company} onChange={set("company")} className={fieldCls} placeholder="Company name" />
          </div>
          <div>
            <label htmlFor="eq-website" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>Website (optional)</label>
            <input id="eq-website" data-testid="enquiry-website" value={form.website} onChange={set("website")} className={fieldCls} placeholder="https://" />
          </div>
          <div>
            <label htmlFor="eq-budget" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>Budget range (optional)</label>
            <select id="eq-budget" data-testid="enquiry-budget" value={form.budget_range} onChange={set("budget_range")} className={fieldCls}>
              <option value="">Prefer not to say</option>
              <option>Under ₹1L / month</option>
              <option>₹1L – ₹5L / month</option>
              <option>₹5L – ₹25L / month</option>
              <option>₹25L+ / month</option>
              <option>Project-based</option>
            </select>
          </div>
        </div>
      )}
      {!isCafe && (
        <div>
          <label htmlFor="eq-message" className={`mb-1.5 block text-xs font-medium ${labelCls}`}>Message *</label>
          <textarea id="eq-message" data-testid="enquiry-message" required={!isCafe} minLength={5} rows={4} value={form.message} onChange={set("message")} className={fieldCls} placeholder="Tell us what you are working towards..." />
        </div>
      )}
      <input type="text" name="company_url" value={form.company_url} onChange={set("company_url")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <div className="space-y-2.5">
        {isCafe && (
          <label className={`flex items-start gap-3 text-xs leading-relaxed ${labelCls}`}>
            <input data-testid="enquiry-whatsapp-consent" type="checkbox" checked={form.whatsapp_consent} onChange={set("whatsapp_consent")} className="mt-0.5 accent-violet" />
            <span>Send me cafe launch updates on WhatsApp. (Optional — you can opt out anytime.)</span>
          </label>
        )}
        <label className={`flex items-start gap-3 text-xs leading-relaxed ${labelCls}`}>
          <input data-testid="enquiry-privacy-consent" type="checkbox" required checked={form.privacy_consent} onChange={set("privacy_consent")} className="mt-0.5 accent-violet" />
          <span>I have read the <a href="/privacy" className="underline decoration-teal/60 underline-offset-2">privacy notice</a> and agree that Ekavrix may store and use these details to respond to my enquiry. *</span>
        </label>
        <label className={`flex items-start gap-3 text-xs leading-relaxed ${labelCls}`}>
          <input data-testid="enquiry-promo-consent" type="checkbox" checked={form.promo_consent} onChange={set("promo_consent")} className="mt-0.5 accent-violet" />
          <span>I would also like occasional updates about Ekavrix ventures. (Optional, and never required.)</span>
        </label>
      </div>
      <p className={`font-mono text-[10px] uppercase tracking-widest ${dark ? "text-slate-500" : "text-slatetext/50"}`}>
        Routed to {routedTo} + saved to our CRM
      </p>
      {state.status === "error" && (
        <div data-testid="enquiry-error" role="alert" className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/[0.08] px-4 py-3 text-sm text-red-300">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /> {state.error}
        </div>
      )}
      <button data-testid="enquiry-submit" type="submit" disabled={state.status === "submitting"}
        className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
        {state.status === "submitting"
          ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>)
          : isCafe ? "Get Launch Updates" : "Send Enquiry"}
      </button>
    </form>
  );
};
